import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TextField, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import { jobApplications, talents } from '../../services/apiService';

// Debounce helper function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const ContactForm = ({
  contacts = [],
  toolsOptions = [],
  onContactsChange,
  title = "Contacts",
  showTitle = true,
  className = "",
  excludeJobApplicationId = null,
  excludeTalentId = null,
  entityType = 'jobApplication', // 'jobApplication' or 'talent'
  validationErrors = {}, // Ошибки валидации
  onClearValidationError = () => {} // Callback для очистки ошибок валидации
}) => {
  const [duplicateChecks, setDuplicateChecks] = useState({});
  const addContact = () => {
    const hasPrimary = contacts.some(contact => contact.is_primary);
    const newContact = {
      tool_id: toolsOptions.length > 0 ? toolsOptions[0].id : null,
      value: '',
      is_primary: !hasPrimary, // If no primary contacts, make new one primary
      is_verified: false
    };

    onContactsChange([...contacts, newContact]);
  };

  const removeContact = (index) => {
    onContactsChange(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index, field, value) => {
    const updatedContacts = contacts.map((contact, i) =>
      i === index ? { ...contact, [field]: value } : contact
    );

    // If setting contact as primary, remove flag from other contacts
    if (field === 'is_primary' && value === true) {
      const finalContacts = updatedContacts.map((contact, i) =>
        i === index ? contact : { ...contact, is_primary: false }
      );
      onContactsChange(finalContacts);
    } else {
      onContactsChange(updatedContacts);
    }

    // Clear validation error for the changed field
    if (field === 'tool_id') {
      onClearValidationError(`contact_${index}_tool_id`);
    } else if (field === 'value') {
      onClearValidationError(`contact_${index}_value`);
    }

    // If changing tool_id or value, check uniqueness
    if ((field === 'value' || field === 'tool_id') && updatedContacts[index].tool_id && updatedContacts[index].value) {
      checkContactUniqueness(index, updatedContacts[index].tool_id, updatedContacts[index].value);
    } else if ((field === 'value' || field === 'tool_id') && !updatedContacts[index].value) {
      // Clear warning if value is empty
      setDuplicateChecks(prev => {
        const newChecks = { ...prev };
        delete newChecks[index];
        return newChecks;
      });
    }
  };

  // Debounced contact uniqueness check
  const checkContactUniqueness = useCallback(
    debounce(async (index, toolId, value) => {
      if (!toolId || !value || value.trim().length < 3) {
        setDuplicateChecks(prev => {
          const newChecks = { ...prev };
          delete newChecks[index];
          return newChecks;
        });
        return;
      }

      try {
        let result;
        if (entityType === 'talent') {
          result = await talents.checkContactUniqueness(
            toolId,
            value,
            excludeTalentId
          );
        } else {
          result = await jobApplications.checkContactUniqueness(
            toolId,
            value,
            excludeJobApplicationId
          );
        }

        if (result.success && !result.isUnique && result.duplicate) {
          setDuplicateChecks(prev => ({
            ...prev,
            [index]: { ...result.duplicate, entityType }
          }));
        } else {
          setDuplicateChecks(prev => {
            const newChecks = { ...prev };
            delete newChecks[index];
            return newChecks;
          });
        }
      } catch (error) {
        console.error('Error checking contact uniqueness:', error);
        // Don't show error to user, just don't display warning
      }
    }, 500),
    [entityType, excludeJobApplicationId, excludeTalentId]
  );

  // Check all contacts on mount or contact list change
  useEffect(() => {
    contacts.forEach((contact, index) => {
      if (contact.tool_id && contact.value && contact.value.trim().length >= 3) {
        checkContactUniqueness(index, contact.tool_id, contact.value);
      }
    });
  }, []); // Run only on mount

  return (
    <div className={className}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">{title}</h3>
          <button
            type="button"
            onClick={addContact}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </button>
        </div>
      )}
      
      {contacts.length === 0 ? (
        <div className="text-center py-8">
          <div className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No contacts added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add Contact" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact, index) => (
            <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">Contact {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeContact(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Contact Type *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={contact.tool_id != null ? String(contact.tool_id) : ''}
                      onChange={(value) => updateContact(index, 'tool_id', value ? Number(value) : null)}
                      placeholder="Select contact type"
                      options={toolsOptions.map((tool) => ({
                        value: String(tool.id),
                        label: tool.name
                      }))}
                      error={!!validationErrors[`contact_${index}_tool_id`]}
                    />
                    {validationErrors[`contact_${index}_tool_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors[`contact_${index}_tool_id`]}
                      </p>
                    )}
                  </div>

                  <div>
                    <TextField
                      label="Contact Value"
                      required
                      fullWidth
                      size="small"
                      type="text"
                      value={contact.value || ''}
                      onChange={(e) => updateContact(index, 'value', e.target.value)}
                      placeholder="Enter contact details"
                      error={!!validationErrors[`contact_${index}_value`] || !!duplicateChecks[index]}
                      helperText={validationErrors[`contact_${index}_value`]}
                    />
                  </div>
                </div>

                {/* Duplicate warning */}
                {duplicateChecks[index] && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-600 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                          This contact is already in use
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                          in {duplicateChecks[index].entityType === 'talent' ? 'talent' : 'job application'}:
                          <span className="font-semibold">
                            {duplicateChecks[index].existingTalent?.name || duplicateChecks[index].existingJobApplication?.name}
                          </span>
                        </p>
                        <Link
                          to={duplicateChecks[index].entityType === 'talent'
                            ? `/talents/${duplicateChecks[index].existingTalent?.id}`
                            : `/job-applications/${duplicateChecks[index].existingJobApplication?.id}`}
                          className="inline-flex items-center text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-200 hover:underline mt-2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Go to {duplicateChecks[index].entityType === 'talent' ? 'talent' : 'job application'} →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-4">
                  <Checkbox
                    label="Primary Contact"
                    color="primary"
                    checked={!!contact.is_primary}
                    onChange={(checked) => updateContact(index, 'is_primary', checked)}
                  />

                  <Checkbox
                    label="Verified"
                    color="primary"
                    checked={!!contact.is_verified}
                    onChange={(checked) => updateContact(index, 'is_verified', checked)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactForm;
